import { useQuery } from '@tanstack/react-query';

import { useCallback, useMemo, useState } from 'react';

import { AddCircle, ContentCopy, Delete, Edit, Refresh } from '@mui/icons-material';
import { Button, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { useApiClient, useIsMobile, useToastService } from '@@common';
import { ConfirmDialog, GalleryHeader, GalleryStatistics } from '@@components';

import { ChangeSetClient, ChangeSetViewModel, ChangeSetViewModelPagingResultModel } from '../../api/api';
import { formatTimestampFull } from '../../common/formatTimestamp';
import ChangeSetStatusChip from './ChangeSetStatusChip';
import CreateChangeSetDialog from './CreateChangeSetDialog';
import EditChangeSetDialog from './EditChangeSetDialog';

const pageSizeOptions = [20, 50];
const initialPaginationModel: GridPaginationModel = {
	pageSize: pageSizeOptions[0],
	page: 0,
};
const initialFilterModel = {
	items: [],
};
const initialSortModel: GridSortModel = [{ field: 'createdDate', sort: 'desc' }];

const sortFieldName: Record<string, string> = {
	name: 'Name',
	description: 'Description',
	status: 'Status',
	createdDate: 'CreatedDate',
};

export default function ChangeSetsListView() {
	const client = useApiClient(ChangeSetClient);
	const toastService = useToastService();
	const isMobile = useIsMobile();

	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(initialPaginationModel);
	const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);
	const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);

	const { data, isLoading, refetch } = useQuery<ChangeSetViewModelPagingResultModel>({
		queryKey: ['changeSets', paginationModel, sortModel, filterModel],
		queryFn: async () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? (sortModel[0].sort === 'asc' ? true : false) : true;
			return client.search(
				undefined, // name filter
				undefined, // isActiveOnly filter
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
			);
		},
	});

	const [openCreateDialog, setOpenCreateDialog] = useState(false);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedChangeSet, setSelectedChangeSet] = useState<ChangeSetViewModel | null>(null);

	const handleEditChangeSet = useCallback((changeSet: ChangeSetViewModel) => {
		setSelectedChangeSet(changeSet);
		setOpenEditDialog(true);
	}, []);

	const handleDeleteChangeSet = useCallback((changeSet: ChangeSetViewModel) => {
		setSelectedChangeSet(changeSet);
		setOpenDeleteDialog(true);
	}, []);

	const handleConfirmDelete = async () => {
		if (!selectedChangeSet) return;

		try {
			// Note: Delete endpoint doesn't exist yet in the API, but we'll prepare for it
			// await client.delete(selectedChangeSet.id);
			toastService.info(`Delete functionality for "${selectedChangeSet.name}" not yet implemented`);
			// refetch();
		} catch (error) {
			console.error('Error deleting ChangeSet:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete ChangeSet: ${errorMessage}`);
		} finally {
			setOpenDeleteDialog(false);
			setSelectedChangeSet(null);
		}
	};

	const handleCancelDelete = () => {
		setOpenDeleteDialog(false);
		setSelectedChangeSet(null);
	};

	const handleCopyId = useCallback(
		(changeSet: ChangeSetViewModel) => {
			navigator.clipboard.writeText(changeSet.id);
			toastService.success('ID copied to clipboard');
		},
		[toastService],
	);

	const rowCount = useMemo(
		() => (data ? paginationModel.page * paginationModel.pageSize + (data.hasNextPage ? paginationModel.pageSize : data.items.length) : 0),
		[paginationModel, data],
	);

	const columns = useMemo<GridColDef<ChangeSetViewModel>[]>(
		() => [
			{
				field: 'name',
				headerName: 'Name',
				sortable: true,
				flex: 2,
				minWidth: isMobile ? 140 : 220,
				renderCell: (params) =>
					isMobile ? (
						<Stack direction="row" spacing={0.75} alignItems="center" sx={{ height: '100%', minWidth: 0, width: '100%' }}>
							<Typography variant="body2" noWrap sx={{ minWidth: 0, flex: 1 }}>
								{params.row.name}
							</Typography>
							{params.row.status && <ChangeSetStatusChip status={params.row.status} size="small" />}
						</Stack>
					) : (
						params.value
					),
			},
			{
				field: 'status',
				headerName: 'Status',
				width: 120,
				sortable: true,
				renderCell: (params) => {
					if (!params.value) return null;
					return <ChangeSetStatusChip status={params.value} />;
				},
			},
			{
				field: 'createdDate',
				headerName: 'Created Date',
				width: 180,
				sortable: true,
				valueGetter: (value) => {
					return value ? formatTimestampFull(value) : '';
				},
			},
			{
				field: 'description',
				headerName: 'Description',
				flex: 5,
				sortable: true,
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				width: isMobile ? 64 : 100,
				getActions: (params) => [
					<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => handleCopyId(params.row)} showInMenu />,
					<GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => handleEditChangeSet(params.row)} showInMenu />,
					<GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => handleDeleteChangeSet(params.row)} showInMenu />,
				],
			},
		],
		[handleCopyId, handleDeleteChangeSet, handleEditChangeSet, isMobile],
	);

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader title="Change Sets" subtitle="Group and manage changes before they are deployed." utilityActions={<Tooltip title="Refresh change sets"><IconButton onClick={() => refetch()}><Refresh /></IconButton></Tooltip>} primaryAction={<Button variant="contained" startIcon={<AddCircle />} onClick={() => setOpenCreateDialog(true)}>Create change set</Button>} statistics={<GalleryStatistics items={[{ label: 'Change sets on this page', value: data?.items.length ?? 0 }]} />} />
			<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items ?? []}
					rowCount={rowCount}
					columns={columns}
					getRowId={(row) => row.id}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					filterModel={filterModel}
					onFilterModelChange={setFilterModel}
					columnVisibilityModel={{
						status: !isMobile,
						createdDate: !isMobile,
						description: !isMobile,
					}}
					density={isMobile ? 'compact' : 'standard'}
					sx={{ border: 0, minWidth: 0 }}
					rowHeight={isMobile ? 48 : undefined}
				/>
			</Paper>
			<CreateChangeSetDialog open={openCreateDialog} setOpen={setOpenCreateDialog} onSuccess={() => refetch()} />
			{selectedChangeSet && (
				<EditChangeSetDialog
					open={openEditDialog}
					setOpen={setOpenEditDialog}
					changeSet={selectedChangeSet}
					onSuccess={() => {
						refetch();
						setSelectedChangeSet(null);
					}}
				/>
			)}
			<ConfirmDialog
				open={openDeleteDialog}
				title="Delete ChangeSet"
				message={
					selectedChangeSet
						? `Are you sure you want to delete "${selectedChangeSet.name}"? This action cannot be undone and may affect any flows or operations that depend on it.`
						: ''
				}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
		</Stack>
	);
}
